import { notFound, redirect } from "@tanstack/react-router";
import { routeHead } from "@/lib/seo/route-head";
import { classifyListSearch, trackingParams } from "@/utils/listSearchParams";
import { resolveVehicleSort } from "@/utils/vehicleSort";
import {
  LANDING_PAGE_SIZE, landingCanonical, landingFaqs, landingRobots, LANDING_PAGES, type LandingSlug,
} from "@/lib/landingPages";
import { landingListingsQueryOptions, landingStatsQueryOptions } from "@/lib/landingQueries";
import type { QueryClient } from "@tanstack/react-query";

type Search = Record<string, string>;
type LoaderData = { count: number; faqs: Array<{ question: string; answer: string }>; page: number; filtered: boolean };

/** Head for a landing page: fixed title, self canonical, server-side robots by count. */
export function landingHead(slug: LandingSlug, data: LoaderData | undefined) {
  const base = routeHead(`/${slug}/`);
  const page = data?.page ?? 1;
  const canonical = landingCanonical(slug, page);
  // Sorted URLs are noindex like /vehicles filters; unknown count stays noindex.
  const robots = data?.filtered ? "noindex, follow" : landingRobots(data?.count);
  const suffix = page > 1 ? ` – pagina ${page}` : "";
  const meta = base.meta
    .filter((m) => m.name !== "robots" && m.property !== "og:url")
    .map((m) => {
      if ("title" in m && Object.keys(m).length === 1) return { title: m.title.replace(/( \| RO Auto Market)$/, `${suffix}$1`) };
      if ((m.name === "title" || m.name === "twitter:title" || m.property === "og:title") && m.content)
        return { ...m, content: m.content.replace(/( \| RO Auto Market)$/, `${suffix}$1`) };
      return m;
    });
  meta.push({ name: "robots", content: robots });
  if (!data?.filtered) meta.push({ property: "og:url", content: canonical });
  const faqs = data?.faqs ?? [];
  return {
    meta,
    links: data?.filtered ? [] : [{ rel: "canonical", href: canonical }],
    scripts: faqs.length
      ? [{ type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
        }) }]
      : [],
  };
}

export async function landingLoader(slug: LandingSlug, queryClient: QueryClient, search: Search): Promise<LoaderData> {
  const kind = classifyListSearch(search);
  if (kind.kind === "invalid-page") throw notFound();
  if (kind.kind === "page-one") {
    const t = trackingParams(search).toString();
    throw redirect({ href: `/${slug}${t ? `?${t}` : ""}`, statusCode: 301 });
  }
  const page = kind.page;
  const sort = resolveVehicleSort(search.sort);
  const [stats, list] = await Promise.all([
    queryClient.ensureQueryData(landingStatsQueryOptions(slug)),
    queryClient.ensureQueryData(landingListingsQueryOptions(slug, sort, page)),
  ]);
  if (page > 1 && page > Math.ceil(list.total / LANDING_PAGE_SIZE)) throw notFound();
  return { count: stats.count, faqs: landingFaqs(LANDING_PAGES[slug], stats), page, filtered: kind.kind === "filtered" };
}

export const validateLandingSearch = (search: Record<string, unknown>): Search =>
  Object.fromEntries(
    Object.entries(search)
      .map(([k, v]) => [k, typeof v === "number" ? String(v) : v])
      .filter((e): e is [string, string] => typeof e[1] === "string"),
  );

export function LandingError({ error }: { error: unknown }) {
  return (
    <main className="container mx-auto max-w-7xl px-4 py-12">
      <h1 className="text-3xl font-bold">Anunțurile nu pot fi încărcate momentan</h1>
      <p className="mt-3 text-muted-foreground" role="alert">{error instanceof Error ? error.message : String(error)}</p>
    </main>
  );
}

export function LandingNotFound() {
  return (
    <main className="container mx-auto max-w-7xl px-4 py-12">
      <h1 className="text-3xl font-bold">Pagina nu a fost găsită</h1>
      <a href="/vehicles" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-medium text-primary-foreground">Vezi toate vehiculele</a>
    </main>
  );
}
